import { Calendar } from "lucide-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import React, { Dispatch, FC, SetStateAction, useEffect } from "react";
import { Orders } from "@/interface/entityInterface";
import { ordersTableCol } from "@/components/common/columns/columns";
import { Card, CardTitle } from "@/components/ui/card";
import { useGetList } from "react-admin";
import { useUserContext } from "@/context/UserContext";
import AssignStatus from "@/components/common/orders/AssignStatus";

interface Props {
    rowClick?: (row: Orders) => void;
    setManifestLength: Dispatch<SetStateAction<number>>;
}

const NewOrders: FC<Props> = ({ rowClick, setManifestLength }) => {
    const {decodedToken} = useUserContext()
    const {data: newOrders = []} = useGetList("taxi-orders/new-orders", {
        pagination: {page: 1, perPage: 5}
    })

    const columns: ColumnDef<Orders>[] = ordersTableCol({
        columns: decodedToken?.permissions.user_role === "owner" ? [
            {
              accessorKey: "accept_reject",
              header: "Accept or Reject",
              cell: ({ row }: any) => <AssignStatus id={row.original.id} orderStatus={row.original.status} />,
            },
        ] : []
    })

    const table = useReactTable({
        data: newOrders,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualPagination: true,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleDetailClick = (e: any, row: Orders) => {
        if (rowClick) {
            if (e.target && e.target.role === "checkbox") {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            rowClick(row);
        }
    };

    useEffect(() => {
        if (newOrders.length > 0) setManifestLength(newOrders.length)
    }, [newOrders]);

    return newOrders.length > 0 ? (
        <div className="border rounded-lg shadow-md p-5">
            <div className="flex items-center space-x-2 mb-3">
                <Calendar className="text-gray-500" />
                <h2 className="text-lg font-semibold">New orders</h2>
            </div>
            <div className="w-full rounded-md border">
                <Table>
                    <TableHeader className="bg-muted sticky top-0 z-10">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {newOrders?.length > 0 ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}
                                    className="cursor-pointer"
                                    onClick={(e: React.MouseEvent) =>
                                        rowClick && handleDetailClick(e, row.original)
                                    }
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    ) : (
        <Card className="p-5 h-full ">
            <div className="flex items-center space-x-2 mb-3">
                <Calendar className="text-gray-500" />
                <h2 className="text-lg font-semibold">New orders</h2>
            </div>
            <CardTitle>No new orders</CardTitle>
        </Card>
    )
};

export default NewOrders;